import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { QueueAction } from '@rossum/api-client/schemas';
import { linebreak } from '../../../lib/formaterValues';
import { memoryFieldsEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import { useHookInterfaces } from '../../extension-interop/hooks/useHookInterfaces';
import { labelsFeatureSelector } from '../../pricing/selectors';
import { visibleActions } from '../actions';
import { createCustomAction } from '../actions/CustomAction';

const allowedNumberOfActionsOfType: Record<QueueAction['type'], number> = {
  add_automation_blocker: 1,
  change_queue: 1,
  hide_field: 1,
  show_field: 1,
  add_validation_source: 1,
  change_status: 1,
  add_label: 1,
  remove_label: 1,
  exclude_from_training: 1,
};

export const useActionOptions = (alreadyUsedActions?: QueueAction[]) => {
  const intl = useIntl();
  const labelsEnabled = useSelector(labelsFeatureSelector);
  const memoryFieldsEnabled = useSelector(memoryFieldsEnabledSelector);

  const { data: hookInterfaces } = useHookInterfaces({
    canInvoke: true,
    type: 'queue_action',
  });

  const selectableActions = useMemo(() => {
    return [
      ...visibleActions
        .filter(action => {
          const actionType = action.serialized.type;
          const maxCount = allowedNumberOfActionsOfType[actionType];

          if (typeof maxCount !== 'number') {
            return true;
          }

          const currentCount =
            alreadyUsedActions?.filter(a => a.type === actionType).length ?? 0;

          return currentCount < maxCount;
        })
        .map(a => a.serialized.type),
      // Custom action is always available
      'custom',
    ];
  }, [alreadyUsedActions]);

  const actionOptions = useMemo(
    () => [
      ...visibleActions
        .filter(a => a.groupIntlKey !== 'labels' || labelsEnabled)
        .filter(a => a.groupIntlKey !== 'memory' || memoryFieldsEnabled)
        .map(a => ({
          ...a,
          name: intl.formatMessage({
            id: `features.queueSettings.rules.actions.${a.intlKey}.name`,
          }),
          description: intl.formatMessage(
            {
              id: `features.queueSettings.rules.actions.${a.intlKey}.description`,
            },
            {
              linebreak,
            }
          ),
          descriptionShort: intl.formatMessage({
            id: `features.queueSettings.rules.actions.${a.intlKey}.descriptionShort`,
          }),
          supportedEvents: a.supportedEvents,
        })),
      ...(hookInterfaces?.results.map(action => ({
        ...createCustomAction(action),
        name: action.name,
        description: action.description,
        descriptionShort: action.description,
      })) ?? []),
    ],
    [hookInterfaces?.results, intl, labelsEnabled, memoryFieldsEnabled]
  );

  return { actionOptions, selectableActions };
};
